import classNames from "classnames";
import React, { HTMLAttributes, useRef, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ArrowWrapper,
  Content,
  ContentWrapper,
  Header,
  HelpText,
  HelpTextList,
  Title,
  TitleWrapper,
  Wrapper
} from "./Accordion.styled";

import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { PropsWithDataQaid } from "src/types/componentProps";

export type AccordionProps = HTMLAttributes<HTMLDivElement> &
  PropsWithDataQaid<{
    /**
     * Set accordion title
     */
    title: string;

    /**
     * Set helper text bellow the title
     */
    helperText: string | [] | boolean;

    /**
     * Set helper image/component on the left of title
     */
    helperImageComponent: () => void | {} | null;

    /**
     * Set accordion to disabled
     */
    disabled: boolean;

    /**
     * alert for rejected reason if any
     */
    rejectedReasonComponent: {} | null;
  }>;

const defaultProps: AccordionProps = {
  title: "",
  helperText: "",
  helperImageComponent: null,
  disabled: false,
  rejectedReasonComponent: null
};

const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>((props, ref) => {
  const {
    children,
    className,
    title,
    helperText,
    helperImageComponent,
    rejectedReasonComponent,
    disabled,
    "data-qaid": dataQaid
  } = props;

  const [height, setHeight] = useState("0px");
  const [isActive, setIsActive] = useState(false);

  const contentRef = useRef(null);
  const accordionClass = classNames("c-accordion", isActive && "is-active", className);

  function toggleAccordion() {
    if (disabled) return;
    const currentActive = isActive;
    setIsActive(!currentActive);
    setHeight(currentActive ? "0px" : `${contentRef.current.scrollHeight}px`);
  }

  React.useEffect(() => {
    setHeight(isActive ? `${contentRef.current.scrollHeight}px` : "0px");
  }, [children, contentRef.current?.scrollHeight]);

  return (
    <Wrapper data-testid="qa-accordion" className={accordionClass} ref={ref} disabled={disabled}>
      <Header
        data-testid="qa-accordion-header"
        className="c-accordion-header"
        onClick={toggleAccordion}
        data-qaid={`${dataQaid}-header`}
      >
        {helperImageComponent}
        <TitleWrapper>
          <Title data-testid="qa-accordion-title" className="c-accordion-header__title" disabled={disabled}>
            {title}
          </Title>
          {helperText &&
            (Array.isArray(helperText) ? (
              <ul className="u-pl-3">
                {helperText.map((item, index) => (
                  <HelpTextList key={index} data-testid="qa-accordion-helper-text">
                    {item}
                  </HelpTextList>
                ))}
              </ul>
            ) : (
              <HelpText data-testid="qa-accordion-helper-text" disabled={disabled}>
                {helperText}
              </HelpText>
            ))}
          {rejectedReasonComponent}
        </TitleWrapper>
        <ArrowWrapper>
          <FontAwesomeIcon icon={faChevronDown} color={disabled ? "grey" : "red"} />
        </ArrowWrapper>
      </Header>
      <ContentWrapper
        data-testid="qa-accordion-content-wrapper"
        ref={contentRef}
        className="c-accordion-wrapper"
        style={{ maxHeight: `${height}` }}
      >
        <Content className="c-accordion-wrapper__content">{children}</Content>
      </ContentWrapper>
    </Wrapper>
  );
});

Accordion.defaultProps = defaultProps;

export default Accordion;
